import type {
  ActForOffer,
  ActForRole,
  ActingFor,
  ApprovalActions,
  ApprovalDetail,
  DocumentView,
} from '@myrewards/contracts';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useState, type ReactElement, type ReactNode } from 'react';
import { meQuery } from '../../api';
import { ActForStep, type ActForDone } from '../../acting-for/ActForStep';
import {
  firstName,
  recordedLine,
  saySoFromTheNotice,
  withNoteAndFiles,
} from '../../acting-for/words';
import { TwoColumns } from '../../shell';
import {
  Button,
  ButtonLink,
  Card,
  formatCount,
  formatMoney,
  Icon,
  scoped,
  TextField,
} from '../../ui';
import type { ActedDone } from './ActedConfirmation';
import { recordedAt, useActingDecision, type ActingRefusal } from './acting';
import { LeadWaitingList, useLeadWaiting } from './LeadWaiting';
import { BudgetCard, NotesCard, PeopleCard, SummaryCard } from './parts';
import { demandOf, overBudgetPools, sectorPool } from './presenter';
import s from '../Ops.module.css';

// APP-148 (LeadApprove board): a Stock Admin deciding a delegate's entry for its Sector Lead, in
// the page. "Approve for Graham", "Return with a note for Graham" (with the required "Note to
// Sam", D22) and "Reject for Graham" each open the step inline; the lead is filled in from the
// award's sector.

type LeadKind = 'approve' | 'return' | 'reject';

const TITLE: Record<LeadKind, string> = {
  approve: 'Approve for',
  return: 'Return for',
  reject: 'Reject for',
};

/** Whose OK it waits on: the sector's lead, or the Executive it was entered for (APP-274). */
const WHOSE: Record<ActForRole, string> = {
  sector_lead: 'a Sector Lead',
  executive: 'an Executive',
};

const VERB: Record<LeadKind, string> = {
  approve: 'Approved',
  return: 'Returned',
  reject: 'Rejected',
};

/** The offer as the page opened it: the lead, with what the lead could do on it. */
export type LeadOffer = ActForOffer & { actions: ApprovalActions };

/**
 * The sector pool's line in the summary (LeadApprove board): "Maritime Airborne · cash left 2026:
 * $161,405 → $156,905. Within budget." or "… Over budget, so it goes on to the Executives."
 */
function poolLine(detail: ApprovalDetail): string | null {
  const year = detail.award.fiscalYear ?? null;
  const pool = sectorPool(detail);
  if (pool === undefined) return null;
  const label = scoped(
    pool.pool.name,
    year === null ? 'cash left this year' : `cash left ${String(year)}`,
  );
  if (pool.status === 'not_set' || pool.remainingAfter === null)
    return `${label}: the budget isn’t set yet, so it goes on to the Executives.`;
  const after = pool.remainingAfter.cashCents;
  const before = after + demandOf(detail, pool.pool.id).cashCents;
  return `${label}: ${formatMoney(before)} → ${formatMoney(after)}. ${
    pool.status === 'over_budget'
      ? 'Over budget, so it goes on to the Executives.'
      : 'Within budget.'
  }`;
}

/** The sector pool as the hero and the confirmation show it: cash left after this, or before. */
function poolAfter(detail: ApprovalDetail, approved: boolean): ActedDone['pool'] {
  const pool = sectorPool(detail);
  if (pool?.remainingAfter == null) return null;
  const after = pool.remainingAfter.cashCents;
  return {
    name: pool.pool.name,
    leftCents: approved ? after : after + demandOf(detail, pool.pool.id).cashCents,
  };
}

/**
 * "Decide for a Sector Lead": the three buttons, and the step for the one pressed, in the card.
 * Pressing another changes what the step does and keeps what's in it: its files, its note for the
 * lead and the note to the delegate. The buttons follow the page's facts; the step keeps the lead
 * it opened with, so a switch turned off meanwhile still says why, until it's closed.
 */
export function LeadDecision({
  detail,
  offer,
  onOpenChange,
  onActed,
  onRefused,
}: {
  detail: ApprovalDetail;
  offer: LeadOffer;
  /** The step opened (with the offer it opened with) or closed: the page keeps it while open. */
  onOpenChange?: ((opened: LeadOffer | null) => void) | undefined;
  onActed: (done: ActedDone) => void;
  onRefused?: ((refusal: ActingRefusal) => void) | undefined;
}): ReactElement {
  const { award } = detail;
  const { data: me } = useSuspenseQuery(meQuery);
  const [step, setStep] = useState<{ kind: LeadKind; offer: LeadOffer } | null>(null);
  const open = step?.kind ?? null;
  const [note, setNote] = useState('');
  const act = useActingDecision(detail, onRefused);
  // What the lead may do now (none once the switch is off), for the buttons.
  const now = detail.actingFor?.actions ?? null;
  const live = detail.actingFor ?? step?.offer ?? offer;
  // APP-274: once the Executive it was entered for has left, any Executive may be named: no name yet.
  const lead = live.fixed ? live.people[0] : undefined;
  const whose = WHOSE[live.role];
  const id = award.displayId ?? 'This entry';
  const creator = award.createdBy.name;
  const creatorFirst = firstName(creator);
  const leadFirst =
    lead === undefined
      ? live.role === 'executive'
        ? 'an Executive'
        : 'the Sector Lead'
      : firstName(lead.name);
  const actor = me.person.displayName;
  const over = overBudgetPools(detail).length > 0;
  // A PAA or TAA is the Executives' decision whatever the budget (APP-070), and an Executive
  // award's their decision outside the app (APP-274, a delegate's entered for one).
  const onward = over || award.type === 'PAA' || award.type === 'TAA' || award.type === 'EDB';

  const start = (kind: LeadKind): void => {
    if (step === null) {
      setNote('');
      onOpenChange?.(offer);
    }
    setStep({ kind, offer: step?.offer ?? offer });
  };
  const close = (): void => {
    setStep(null);
    setNote('');
    onOpenChange?.(null);
  };

  const confirm = async (actingFor: ActingFor, done: ActForDone): Promise<void> => {
    if (open === null) return;
    const text = note.trim();
    const kind = open;
    const decision =
      kind === 'approve'
        ? { action: 'approve' as const, version: award.version, actingFor }
        : kind === 'return'
          ? { action: 'return' as const, version: award.version, note: text, actingFor }
          : { action: 'reject' as const, version: award.version, reason: text, actingFor };
    await act(decision, (next) => {
      onActed({
        awardId: award.id,
        role: live.role,
        kind,
        person: done.person,
        note: done.note,
        files: done.files,
        at: recordedAt(next),
        topUps: [],
        pool: poolAfter(detail, kind === 'approve' && !over),
        goesTo: kind === 'approve' ? (onward ? 'executives' : 'stock_admin') : null,
        toCreator: kind === 'approve' ? null : text,
      });
    });
  };

  const summary =
    (kind: LeadKind) =>
    (_personId: string | null, added: { note: boolean; files: number }): ReactNode => {
      const told = `${lead === undefined ? 'The person you name' : leadFirst} gets a notice in the bell and by email${withNoteAndFiles(added.note, added.files)}.`;
      if (kind === 'return')
        return (
          <>
            <li>
              {id} goes back to {creator} as a draft, with your note to {creatorFirst}, to change
              and send again.
            </li>
            <li>{told}</li>
          </>
        );
      if (kind === 'reject')
        return (
          <>
            <li>
              {id} is turned down and won’t be paid. {creator} is told, with your reason.
            </li>
            <li>{told}</li>
          </>
        );
      const line = poolLine(detail);
      return (
        <>
          <li>
            {onward
              ? `${id} is approved and goes on to the Executives${over ? ' for a top-up' : ', who decide it'}.`
              : `${id} is approved and goes to the Stock Admin team.`}{' '}
            {creator} hears it was approved, as usual.
          </li>
          {line !== null && <li>{line}</li>}
          <li>{told}</li>
        </>
      );
    };

  const buttons: { kind: LeadKind; label: string; allowed: boolean }[] = [
    { kind: 'approve', label: `Approve for ${leadFirst}`, allowed: now?.approve === true },
    {
      kind: 'return',
      label: `Return with a note for ${leadFirst}`,
      allowed: now?.return === true,
    },
    { kind: 'reject', label: `Reject for ${leadFirst}`, allowed: now?.reject === true },
  ];

  return (
    <Card
      title={`Decide for ${whose}`}
      accent="cyan"
      helpAnchor="decide-for-lead"
      help={{
        shows: `${id} waits for ${leadFirst}’s OK. When ${leadFirst} asks you to, you can decide it for them.`,
        canDo: `Approve it, return it to ${creator} with a note to change, or reject it with a reason. It’s recorded as your action, done for ${lead === undefined ? 'them' : leadFirst}, and ${lead === undefined ? 'they’re' : `${leadFirst} is`} told.`,
      }}
    >
      <div className={s.actionsRow}>
        {buttons
          .filter((b) => b.allowed)
          .map((b) => (
            <Button
              key={b.kind}
              variant={b.kind === 'approve' ? 'cyan' : 'ghost'}
              {...(b.kind === 'approve' && { icon: <Icon name="check" size={18} /> })}
              onPress={() => {
                start(b.kind);
              }}
            >
              {b.label}
            </Button>
          ))}
      </div>
      {open !== null && (
        <ActForStep
          inline
          title={`${TITLE[open]} ${whose}`}
          actorName={actor}
          offer={live}
          firstNameOf={(personId) =>
            firstName(live.people.find((p) => p.id === personId)?.name ?? leadFirst)
          }
          {...(live.role === 'executive' && { fixedHint: 'The Executive it was entered for' })}
          summaryTitle={
            open === 'approve'
              ? 'When you approve'
              : open === 'return'
                ? 'When you return it'
                : 'When you reject'
          }
          summary={summary(open)}
          recorded={(p) => recordedLine(VERB[open], actor, p, live.role)}
          confirmLabel={(p) =>
            `${open === 'approve' ? 'Approve' : open === 'return' ? 'Return' : 'Reject'} for ${firstName(p.name)}`
          }
          // Sent back or turned down: nothing is held, and the lead can say so. Approving has no
          // footnote (LeadApprove board).
          ifNotWanted={open === 'approve' ? null : saySoFromTheNotice}
          ready={open === 'approve' || note.trim() !== ''}
          onConfirm={confirm}
          onCancel={close}
        >
          {open !== 'approve' && (
            <TextField
              label={open === 'return' ? `Note to ${creatorFirst}` : `Reason for ${creatorFirst}`}
              description={`${creator} reads it with the entry. It’s yours to them, separate from the note for ${lead === undefined ? 'the Executive' : leadFirst}.`}
              multiline
              isRequired
              value={note}
              onChange={setNote}
            />
          )}
        </ActForStep>
      )}
    </Card>
  );
}

/** "Also waiting on Graham · N": the lead's other work, or "Nothing else is waiting on Graham." */
export function AlsoWaitingOnLead({
  detail,
  lead,
}: {
  detail: ApprovalDetail;
  lead: { id: string; name: string };
}): ReactElement {
  const first = firstName(lead.name);
  const rows = useLeadWaiting(lead.id, detail);
  return (
    <Card
      title={`Also waiting on ${first} · ${formatCount(rows.length)}`}
      titleSize="md"
      help={{
        shows: `Other work waiting on ${lead.name}, oldest first: delegates’ entries to approve and awards returned to fix.`,
        canDo: `When ${first} asks you to, open one and do it for ${first}.`,
      }}
    >
      <LeadWaitingList rows={rows} first={first} />
      <ButtonLink href="/approvals" variant="ghost" size="sm" block>
        Waiting on others
      </ButtonLink>
    </Card>
  );
}

/**
 * A delegate's entry as a Stock Admin sees it, to decide for its Sector Lead (LeadApprove board):
 * the entry and its people, "Decide for a Sector Lead", and beside them the budget, the
 * delegate's note and files, and what else waits on the lead.
 */
export function LeadEntryView({
  detail,
  offer,
  documents,
  onOpenChange,
  onActed,
  onRefused,
}: {
  detail: ApprovalDetail;
  offer: LeadOffer;
  documents: readonly DocumentView[];
  onOpenChange?: ((opened: LeadOffer | null) => void) | undefined;
  onActed: (done: ActedDone) => void;
  onRefused?: ((refusal: ActingRefusal) => void) | undefined;
}): ReactElement {
  const lead = offer.fixed ? offer.people[0] : undefined;
  const nobody = offer.role === 'executive' ? 'the Executives' : 'the Sector Lead';
  const over = overBudgetPools(detail).length > 0;
  return (
    <TwoColumns
      sideLabel={`Budget, the entry’s note and what else waits on ${lead === undefined ? nobody : firstName(lead.name)}`}
      main={
        <>
          <SummaryCard
            detail={detail}
            status={{
              label: lead === undefined ? `Waiting on ${nobody}` : `Waiting on ${lead.name}`,
              tone: 'waiting',
            }}
          />
          <PeopleCard detail={detail} />
          <LeadDecision
            detail={detail}
            offer={offer}
            onOpenChange={onOpenChange}
            onActed={onActed}
            onRefused={onRefused}
          />
        </>
      }
      side={
        <>
          <BudgetCard
            detail={detail}
            note={
              over
                ? 'This entry goes over budget: approving it sends it on to the Executives to approve a top-up.'
                : 'If an entry like this ran over budget, approving it would send it on to the Executives.'
            }
          />
          <NotesCard detail={detail} documents={documents} />
          {lead !== undefined && <AlsoWaitingOnLead detail={detail} lead={lead} />}
        </>
      }
    />
  );
}
